import type {
  BrandSpecialQualifyingStatus,
  BrandSpecials,
  SpecialQualifyingStatus,
} from '@iheartjane/data-access';
import { FLAGS } from '@iheartjane/feature-flags';
import type {
  CompareProduct,
  SpecialCalloutItemData,
  SpecialWeightRules,
} from '@iheartjane/menu';
import {
  SpecialCalloutSection,
  StateWarnings,
  containProductImages,
  getInStockWeights,
  getMenuPdpApplicableGoldTagLabel,
  getSpecialApplicableWeights,
  getSpecialSlug,
  hideStrikethroughPrice,
} from '@iheartjane/menu';
import {
  Accordion,
  Flex,
  JaneGoldIcon,
  Tag,
  TagFilledIcon,
  Typography /** @todo remove media query hook */,
  // eslint-disable-next-line no-restricted-imports
  useMobileMediaQuery,
} from '@iheartjane/reefer';
import type { Breadcrumb, PriceId } from '@iheartjane/types';
import { clsx } from 'clsx';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { BloomLink } from '@jane/bloom/components/bloomLink';
import { Breadcrumbs } from '@jane/bloom/components/breadcrumbs/breadcrumbs';
import { SpecialBadge } from '@jane/bloom/components/productCard/components/specialBadge';
import { ReviewStars } from '@jane/bloom/components/reviewStars/reviewStars';
import { useFullDestination } from '@jane/bloom/hooks/navigation/useFullDestination';
import { useCustomLanguage } from '@jane/bloom/hooks/useCustomLanguage';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import { trackStoreSpecialPromptTap } from '@jane/bloom/services/tracking/track';
import type { StoreSpecials } from '@jane/bloom/types/schemas/marketplace/storeSpecials';
import { getJaneGoldTitle } from '@jane/bloom/utils/gold/getJaneGoldTitle';
import {
  areAllDiscountsTheSamePercentage,
  getSpecialBadgeLabelsForMultipleWeights,
} from '@jane/bloom/utils/product/areAllDiscountsTheSamePercentage';
import {
  getSortedTerpenes,
  getTopFlavors,
} from '@jane/bloom/utils/terpenes/terpenes';
import { buildCompareProductFromDetail } from '@jane/bloom/viewModels/compareProduct';
import {
  getPriceBeforeDiscounts,
  getPriceIncludingDiscounts,
} from '@jane/bloom/viewModels/getPrice';
import type { ProductDetailViewModel } from '@jane/bloom/viewModels/productDetail';
import type { ProductReviewsSummaryViewModel } from '@jane/bloom/viewModels/productReviews';
import type { MmpPriceId } from '@jane/shared/types';

import { MultiParagraph } from './multiParagraph';
import { OtherLocationsRow } from './otherLocationsRow';
import { ComparePill } from './productDetailCard/comparePill';
import { CompareProductButton } from './productDetailCard/compareProductButton';
import { JaneGoldCallout } from './productDetailCard/janeGoldCallout';
import { JaneGoldCalloutSection } from './productDetailCard/janeGoldCalloutSection';
import { JaneGoldSection } from './productDetailCard/janeGoldSection';
import { ImageSkeleton } from './productDetailCard/loaders/imageSkeleton';
import { PriceSkeleton } from './productDetailCard/loaders/priceSkeleton';
import { ProductAddToCart } from './productDetailCard/productAddToCart';
import { ProductDetailAlert } from './productDetailCard/productDetailAlert';
import { ProductDetails } from './productDetailCard/productDetails';
import { ShareProduct } from './productDetailCard/shareProduct';
import { TopFeelings } from './productDetailCard/topFeelings';
import { TopFlavors } from './productDetailCard/topFlavors';
import { TopTerpenes } from './productDetailCard/topTerpenes';
import { ProductImageCarousel } from './productImageCarousel/productImageCarousel';

import styles from './productDetailCard.module.css';

export type ProductDetailProps = {
  applicableStoreSpecialIds: Set<number>;
  brandSpecials: BrandSpecials;
  brandSpecialsQualifyingStatuses: Record<string, BrandSpecialQualifyingStatus>;
  breadcrumbs: Breadcrumb[];
  californiaProp65Warnings: boolean;
  isAvailableInTheArea?: boolean;
  isGoldUser: boolean;
  isOutOfStock?: boolean;
  isSignedIn: boolean;
  isStoreChanging?: boolean;
  productDetail: ProductDetailViewModel;
  productReviewsSummary: ProductReviewsSummaryViewModel;
  scrollToReviewSection: () => void;
  shouldShowGold: boolean;
  storeSpecials: StoreSpecials;
  storeSpecialsQualifyingStatuses: Record<string, SpecialQualifyingStatus>;
};

export const ProductDetailCard = ({
  applicableStoreSpecialIds,
  brandSpecials,
  brandSpecialsQualifyingStatuses,
  breadcrumbs,
  californiaProp65Warnings,
  isAvailableInTheArea = true,
  isGoldUser,
  isOutOfStock = false,
  isSignedIn,
  isStoreChanging = false,
  productDetail,
  productReviewsSummary,
  scrollToReviewSection,
  shouldShowGold,
  storeSpecials,
  storeSpecialsQualifyingStatuses,
}: ProductDetailProps) => {
  const { product, productImagesSrcSets, showSocialShare } = productDetail;
  const { cart, featureFlags, pinnedStore } = useRootLoaderData();
  const [quantity, setQuantity] = useState(1);
  const [currentWeight, setCurrentWeight] = useState<MmpPriceId>(
    product.available_weights?.[0]
  );
  const specialsRedesign = featureFlags[FLAGS.c3SpecialsRedesignMenu];
  const extendedDealsLabelsEnabled = featureFlags[FLAGS.c1ExtendedDealsLabels];
  const containImages =
    !!featureFlags[FLAGS.c3CarouselImageFix] &&
    containProductImages(pinnedStore.state ?? '');

  useEffect(() => {
    setCurrentWeight(product.available_weights?.[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.available_weights?.[0]]);

  const currentPrice = getPriceIncludingDiscounts(product, currentWeight);

  const priceBeforeDiscounts = getPriceBeforeDiscounts(product, currentWeight);

  const hasSpecialApplied =
    product.special_amount || product.special_id || product.special_title;

  let specialBadgeLabels;
  if (product.available_weights.length > 1) {
    const allDiscountsAreSame = areAllDiscountsTheSamePercentage(product);

    if (!allDiscountsAreSame) {
      specialBadgeLabels = getSpecialBadgeLabelsForMultipleWeights(product);
    }
  }

  const currentWeightAsPriceId = currentWeight
    ? (currentWeight.replace(' ', '_') as PriceId)
    : 'each';

  const topFlavors = useMemo(
    () => getTopFlavors(product.lab_results, currentWeightAsPriceId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [product.lab_results, currentWeight]
  );

  const sortedTerpenes = useMemo(
    () => getSortedTerpenes(product.lab_results, currentWeightAsPriceId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [product.lab_results, currentWeight]
  );

  // Get weight-specific THC and CBD percentages
  const { percentageCBD, percentageTHC } = useMemo(() => {
    const inventoryPotencies = product.inventory_potencies || [];
    const inventoryPotency = inventoryPotencies.find(
      (potency) => potency['price_id'] === currentWeightAsPriceId
    );

    return {
      percentageCBD: Number(
        inventoryPotency?.['cbd_potency'] || product.percent_cbd || 0
      ),
      percentageTHC: Number(
        inventoryPotency?.['thc_potency'] || product.percent_thc || 0
      ),
    };
  }, [
    currentWeightAsPriceId,
    product.inventory_potencies,
    product.percent_cbd,
    product.percent_thc,
  ]);

  const isMobile = useMobileMediaQuery({});

  const compareEnabled = featureFlags[FLAGS.c1CompareProducts];
  const customLanguage = useCustomLanguage();

  const compareProduct = useMemo<CompareProduct | null>(() => {
    if (!compareEnabled) return null;
    return buildCompareProductFromDetail({
      brandSpecials,
      bundleTag: Boolean(featureFlags[FLAGS.c1BundleTag]),
      cart,
      customLanguage,
      pinnedStore,
      product,
      shouldShowGold,
      storeSpecials,
    });
  }, [
    brandSpecials,
    cart,
    compareEnabled,
    customLanguage,
    featureFlags,
    pinnedStore,
    product,
    shouldShowGold,
    storeSpecials,
  ]);

  const showOtherLocationsBelowWarning =
    productDetail.storesCarryingProduct.length > 0 &&
    isAvailableInTheArea &&
    !isOutOfStock;

  const showOtherLocationsBelowToast =
    productDetail.storesCarryingProduct.length > 0 &&
    (!isAvailableInTheArea || isOutOfStock);

  // applicable_brand_special_ids are sorted on the backend by best discount
  const applicableBrandSpecials = useMemo(() => {
    return productDetail.product.applicable_brand_special_ids
      ?.map((id) => brandSpecials.find((special) => special.id === id))
      .filter(Boolean) as BrandSpecials;
  }, [productDetail.product.applicable_brand_special_ids, brandSpecials]);

  const sortedBrandSpecials = useMemo(() => {
    return applicableBrandSpecials?.sort((a, b) => {
      if (a?.special_type === 'product') {
        return -1;
      }
      if (b?.special_type === 'product') {
        return 1;
      }
      return 0;
    });
  }, [applicableBrandSpecials]);

  const generatePath = useFullDestination();
  const navigate = useNavigate();

  const complianceLanguage =
    pinnedStore.store_compliance_language_settings ?? undefined;

  const inStockWeights = useMemo(
    () => getInStockWeights(product.available_weights, product.amount),
    [product.amount, product.available_weights]
  );

  const specialCalloutItems: SpecialCalloutItemData[] = useMemo(() => {
    return storeSpecials
      .filter(({ id }) => applicableStoreSpecialIds.has(id))
      .map((special) => ({
        applicableWeights: getSpecialApplicableWeights({
          inStockWeights,
          rules: special.rules as SpecialWeightRules,
        }),
        id: special.id,
        isApplied:
          special.special_type === 'product'
            ? true
            : storeSpecialsQualifyingStatuses[special.id]?.qualifying_status ===
              'applied',
        isQualified:
          storeSpecialsQualifyingStatuses[special.id]?.qualifying_status ===
          'qualified',
        special_type: special.special_type,
        title: special.title,
      }));
  }, [
    applicableStoreSpecialIds,
    inStockWeights,
    storeSpecials,
    storeSpecialsQualifyingStatuses,
  ]);

  const handleSpecialClick = useCallback(
    (special: SpecialCalloutItemData) => {
      const fullSpecial = storeSpecials.find((s) => s.id === special.id);
      if (fullSpecial) {
        trackStoreSpecialPromptTap({ special: fullSpecial });
      }
      const linkPath = generatePath(
        `/deals/${special.id}/${getSpecialSlug(special.title)}`
      );
      navigate(linkPath);
    },
    [generatePath, navigate, storeSpecials]
  );

  const brandSpecial = sortedBrandSpecials?.[0];

  const janeGoldTagLabel = useMemo(() => {
    return getMenuPdpApplicableGoldTagLabel({
      brandSpecials: applicableBrandSpecials,
      menuProduct: productDetail.product,
      selectedWeight: currentWeight as PriceId,
    });
  }, [
    applicableBrandSpecials,
    brandSpecial,
    currentWeight,
    productDetail.product,
  ]);

  const janeGoldTitle = getJaneGoldTitle({
    menuProduct: product,
    priorityValueKey: 'title',
  });

  const isSegmentedDeal =
    !!product.has_brand_discount &&
    ((Array.isArray(product.applicable_brand_specials_excluded_user_segments) &&
      product.applicable_brand_specials_excluded_user_segments.length > 0) ||
      (Array.isArray(
        product.applicable_brand_specials_included_user_segments
      ) &&
        product.applicable_brand_specials_included_user_segments.length > 0));

  const shouldShowWarning =
    pinnedStore.state === 'California' || pinnedStore.state === 'Kentucky';

  return (
    <div className={styles['pdpCardWrapper']}>
      <Breadcrumbs
        breadcrumbs={breadcrumbs}
        className={styles['breadcrumbs']}
      />

      <div className={styles['pdpCardContainer']}>
        <div className={styles['pdpCardImage']}>
          <div className={styles['productImgContainer']}>
            {isStoreChanging && <ImageSkeleton />}
            {!isStoreChanging && (
              <ProductImageCarousel
                /** Trigger a re-render when the product changes. */
                key={product.base_image}
                containImages={containImages}
                images={productImagesSrcSets}
                productName={product.name}
                overlayContent={
                  <>
                    {compareProduct &&
                      isAvailableInTheArea &&
                      !isOutOfStock && (
                        <CompareProductButton
                          className={clsx(styles['compareDesktopButton'], {
                            [styles['compareDesktopButton__raised']]:
                              productImagesSrcSets.length > 1,
                          })}
                          compareProduct={compareProduct}
                          testId="compare-desktop-button"
                        />
                      )}
                    {(!isAvailableInTheArea || isOutOfStock) && isMobile && (
                      <ProductDetailAlert label="This product isn't available at your selected location." />
                    )}
                    {hasSpecialApplied && (
                      <SpecialBadge
                        containerClassName={styles['specialBadgeContainer']}
                        label={
                          specialBadgeLabels &&
                          specialBadgeLabels[currentWeight]
                            ? specialBadgeLabels[currentWeight]
                            : product.specialLabel
                        }
                      />
                    )}
                  </>
                }
              />
            )}
          </div>
        </div>
        <div className={styles['pdpCardDetails']}>
          <div className={styles['pdpNamesAndPrices']}>
            <Flex flexDirection="column">
              <Typography variant="h2-semibold" color="text-main" as="h1">
                {product.name}
              </Typography>
            </Flex>

            <div className={styles['pdpCardContainerPrice']}>
              {isStoreChanging && <PriceSkeleton />}
              {!isStoreChanging && (
                <div className={styles['pdpCardPrice']}>
                  {currentPrice && (
                    <Typography
                      color="grays-dark"
                      className={
                        hasSpecialApplied
                          ? styles['productDetailCardSpecialsText']
                          : ''
                      }
                      variant="h2-semibold"
                    >
                      ${(currentPrice * quantity).toFixed(2)}
                    </Typography>
                  )}

                  {product.special_amount &&
                    priceBeforeDiscounts > 0 &&
                    priceBeforeDiscounts !== currentPrice &&
                    !hideStrikethroughPrice(pinnedStore.state) && (
                      <Typography color="grays-mid" strikeThrough>
                        ${(priceBeforeDiscounts * quantity).toFixed(2)}
                      </Typography>
                    )}
                </div>
              )}
            </div>
          </div>
          <div>
            <div className={styles['pdpBrandAndTag']}>
              {product.brand && (
                <BloomLink
                  to={`/featured?brand=${product.brand}`}
                  variant="reefer-underline"
                >
                  <Typography>{product.brand}</Typography>
                </BloomLink>
              )}

              {shouldShowGold && janeGoldTagLabel && (
                <Tag
                  background="gold"
                  color="grays-black"
                  icon={<JaneGoldIcon size="sm" />}
                  label={janeGoldTagLabel}
                />
              )}
            </div>

            {product.brand_subtype && (
              <Typography color="grays-mid">{product.brand_subtype}</Typography>
            )}

            {productReviewsSummary.hasReviews && (
              <ReviewStars
                rating={productReviewsSummary.aggregateRating}
                reviewCount={productReviewsSummary.totalReviewCount}
                scrollToReviews={scrollToReviewSection}
              />
            )}
            {product.special_amount && currentPrice && (
              <div className={styles['pdpCardSpecials']}>
                <TagFilledIcon size="sm" className={styles['specialIcon']} />
                <Typography color="grays-dark" maxLines={2}>
                  {product.special_amount}
                  {product.special_title && ` - ${product.special_title}`}
                </Typography>
              </div>
            )}
          </div>

          {(!isAvailableInTheArea || isOutOfStock) && !isMobile && (
            <ProductDetailAlert label="This product isn't available at your selected location." />
          )}

          {showOtherLocationsBelowToast && (
            <OtherLocationsRow
              locations={productDetail.storesCarryingProduct}
            />
          )}

          {isAvailableInTheArea && !isOutOfStock && (
            <div className={styles['pdpWeightSelectorWrapper']}>
              {compareProduct && (
                <ComparePill compareProduct={compareProduct} />
              )}
              <ProductAddToCart
                availableWeights={product.available_weights}
                currentWeight={currentWeight}
                isAvailableInTheArea={isAvailableInTheArea}
                isOutOfStock={isOutOfStock}
                isStoreChanging={isStoreChanging}
                product={product}
                quantity={quantity}
                setCurrentWeight={setCurrentWeight}
                setQuantity={setQuantity}
              />
            </div>
          )}

          {shouldShowGold && janeGoldTagLabel && sortedBrandSpecials?.length ? (
            <div className={styles['janeGoldContainer']}>
              <div className={styles['janeGoldHeader']}>
                <JaneGoldIcon size="sm" />
                <Typography variant="body-semibold">Jane Gold</Typography>
              </div>
              {sortedBrandSpecials.map((brandSpecial) =>
                specialsRedesign ? (
                  <JaneGoldCalloutSection
                    key={brandSpecial?.id}
                    brandSpecial={brandSpecial}
                    cartBrandDiscounts={cart.brand_discounts ?? []}
                    inStockWeights={inStockWeights}
                    qualifyingStatus={
                      brandSpecial
                        ? brandSpecialsQualifyingStatuses[brandSpecial.id]
                        : undefined
                    }
                    isSegmentedDeal={isSegmentedDeal}
                    isSignedIn={isSignedIn}
                  />
                ) : (
                  <JaneGoldCallout
                    key={brandSpecial?.id}
                    brandSpecial={brandSpecial}
                    cartBrandDiscounts={cart.brand_discounts ?? []}
                    isMobile={isMobile}
                    qualifyingStatus={
                      brandSpecial
                        ? brandSpecialsQualifyingStatuses[brandSpecial.id]
                        : undefined
                    }
                    isSegmentedDeal={isSegmentedDeal}
                    isSignedIn={isSignedIn}
                    brand={brandSpecial.product_brand}
                    isGoldUser={isGoldUser}
                  />
                )
              )}
            </div>
          ) : (
            shouldShowGold &&
            janeGoldTitle && (
              <JaneGoldSection
                title={janeGoldTitle}
                isGoldUser={isGoldUser}
                isSegmentedDeal={isSegmentedDeal}
                isSignedIn={isSignedIn}
                brand={{
                  logo_url: product.brand_logo_url,
                  name: product.brand || null,
                }}
              />
            )
          )}

          {specialsRedesign && (
            <div className={styles['janeGoldContainer']}>
              <SpecialCalloutSection
                complianceLanguageSettings={complianceLanguage}
                extendedDealsLabelsEnabled={extendedDealsLabelsEnabled}
                onSpecialClick={handleSpecialClick}
                sectionLabel={complianceLanguage?.['specials'] || 'Deals'}
                specials={specialCalloutItems}
              />
            </div>
          )}

          <ProductDetails
            category={product.category}
            percentageCBD={percentageCBD}
            percentageTHC={percentageTHC}
          />

          {productReviewsSummary.showPDPEffects && (
            <TopFeelings feelings={productReviewsSummary.feelingsCards} />
          )}

          {topFlavors.length > 0 && <TopFlavors flavors={topFlavors} />}

          {sortedTerpenes.length > 0 && (
            <TopTerpenes sortedTerpenes={sortedTerpenes} />
          )}

          {!pinnedStore.pdp_hide_description ? (
            pinnedStore.state === 'Ohio' ? (
              <Accordion>
                <Accordion.Item id="description">
                  <Accordion.Heading mt={24}>
                    <Flex my={0} pr={8} gap={8} alignItems="center">
                      <Typography variant="body-semibold">
                        Description
                      </Typography>
                      <Accordion.Indicator iconSize="sm" />
                    </Flex>
                  </Accordion.Heading>
                  <Accordion.Content mt={8}>
                    <MultiParagraph>{product.description}</MultiParagraph>
                  </Accordion.Content>
                </Accordion.Item>
              </Accordion>
            ) : (
              <>
                <Typography
                  mb={8}
                  mt={24}
                  variant="body-semibold"
                  color="text-main"
                >
                  Description
                </Typography>
                <MultiParagraph>{product.description}</MultiParagraph>
              </>
            )
          ) : null}

          {!!pinnedStore.state && shouldShowWarning && (
            <StateWarnings
              expandedByDefault={
                pinnedStore.state === 'California' && californiaProp65Warnings
              }
              productKind={product.kind}
              state={pinnedStore.state}
              mt={24}
              mb={8}
            />
          )}

          {showOtherLocationsBelowWarning && (
            <OtherLocationsRow
              locations={productDetail.storesCarryingProduct}
            />
          )}

          {showSocialShare && <ShareProduct name={product.name} />}
        </div>
      </div>
    </div>
  );
};
